import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { Grid, GridConfig, GridRowsSelectedEvent } from '@inavista/components/grid';
import { LOCAL_ROWS_URL, LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { AssignmentGridsConfigurations } from '../../model/user-profile.model';
import { ISignal } from '../../../../shared/models/app.model';
import { CommonService } from '../../../../shared/services/common.service';

// Sales section → Assignments: one grid per level (Region, District, Territory), on @inavista/components. The rows are the
// parent's (configs.updatedDataSource, ticked by isChecked) and go through LocalRowsGridApi (the approved local-rows
// seam): the parent's cascade rules are unchanged. Each tick change becomes the old signal: one row = Selecting/Deselecting
// Single<Level>, several (the header box) = Selecting/Deselecting All<Level>, with checkedData as the old grid gave it
// (the row, or the rows). Unticking regions or districts asks first, and a "no" puts the ticks back. Read-only rows and a
// disabled section can't be ticked (the reason is on the box); with a read-only row the header box doesn't apply, as the
// old disabled header box.
const LEVEL_PLURAL: Record<string, string> = { Region: 'Regions', District: 'Districts', Territory: 'Territories' };

@Component({
    selector: 'app-assignment-grid',
    templateUrl: './assignment-grid.component.html',
    styleUrls: ['./assignment-grid.component.scss'],
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AssignmentGridComponent implements OnInit, DoCheck {
  @Input('configs') configs!: AssignmentGridsConfigurations;
  @Input('isSectionEnabled') isSectionEnabled: boolean = false;
  @Output() outputSignal = new EventEmitter<ISignal>();
  @ViewChild('assignmentsGrid') grid?: Grid;

  private readonly commonService = inject(CommonService);
  private readonly gridRows = inject(LocalRowsGridApi);
  private readonly cdr = inject(ChangeDetectorRef);

  gridConfig!: GridConfig;
  count = 0;
  selected = 0;
  // What the grid shows: the parent's array and its ticks, to notice when the parent changes either.
  private shownRows: any[] | null = null;
  private shownTicks = '';
  private shownEnabled: boolean | null = null;

  ngOnInit() {
    const url = `${LOCAL_ROWS_URL}user-profile/assignments/${this.configs.gridId}`;
    const columns = this.configs.columns.filter((c: any) => c.field && c.field !== 'isChecked').map((c: any) => c.field as string);
    this.gridRows.columns.set(LocalRowsGridApi.schema([['id', 'int'], ...columns.map(c => [c, 'varchar'] as [string, string])]));
    this.gridConfig = {
      apiBaseUrl: url,
      keyColumn: 'id',
      clientSide: true,
      rowHeight: 26,
      stretchColumns: true,
      enableSearch: true,
      enableFiltering: false,
      enableFitColumns: true,
      enableResetLayout: true,
      selectable: true,
      rowCheckLockedFn: row => (!this.isSectionEnabled ? 'This section is disabled' : row['isReadOnly'] ? 'Read only' : false),
      defaultColumnOrder: columns,
      hiddenByDefaultColumns: ['id'],
      columnLabels: Object.fromEntries(this.configs.columns.filter((c: any) => c.headerText).map((c: any) => [c.field, c.headerText])),
      columnAlign: Object.fromEntries(columns.map(c => [c, { header: { horizontal: 'left' as const } }])),
      emptyStateMessage: `No ${LEVEL_PLURAL[this.configs.gridName]?.toLowerCase() ?? 'rows'} to assign.`,
      persistenceKey: `rims.users.assignments-${this.configs.gridName.toLowerCase()}`,
      persistenceVersion: 1,
    };
  }

  // The parent replaces configs.updatedDataSource (and ticks rows) without a new configs object: check each cycle.
  ngDoCheck() {
    const rows = (this.configs?.updatedDataSource as any[]) ?? [];
    const ticks = this.tickedIds(rows).join(',');
    if (rows === this.shownRows && ticks === this.shownTicks && this.isSectionEnabled === this.shownEnabled) return;
    const rowsChanged = rows !== this.shownRows || this.isSectionEnabled !== this.shownEnabled;
    this.shownRows = rows;
    this.shownTicks = ticks;
    this.shownEnabled = this.isSectionEnabled;
    this.count = rows.length;
    this.selected = this.tickedIds(rows).length;
    if (rowsChanged) {
      this.gridRows.rows.set(rows.map(r => ({ ...r })));
      this.grid?.refresh({ keepScroll: true });
    }
    // After the refresh has the rows: tick them, without it counting as the user's change.
    queueMicrotask(() => this.grid?.setCheckedKeys(this.tickedIds(rows), { emit: false }));
    this.cdr.markForCheck();
  }

  private tickedIds(rows: any[]) {
    return rows.filter(r => r?.isChecked === true).map(r => r.id);
  }

  onRowsSelected(event: GridRowsSelectedEvent) {
    const rows: any[] = (this.configs?.updatedDataSource as any[]) ?? [];
    const before = new Set(this.tickedIds(rows));
    const now = new Set(event.rows.map(r => r['id']));
    const added = rows.filter(r => now.has(r.id) && !before.has(r.id));
    const removed = rows.filter(r => before.has(r.id) && !now.has(r.id));
    const revert = () => this.grid?.setCheckedKeys([...before], { emit: false });
    if (added.length === 0 && removed.length === 0) return;
    const many = added.length + removed.length > 1;
    if (many && rows.some(r => r?.isReadOnly)) {
      revert();
      this.commonService.showNotification('info', `Some ${LEVEL_PLURAL[this.configs.gridName]?.toLowerCase()} are read only: tick them one by one.`);
      return;
    }
    const level = this.configs.gridName;
    if (added.length > 0) {
      this.outputSignal.emit({ action: many ? `SelectingAll${LEVEL_PLURAL[level]}` : `SelectingSingle${level}`, data: { checkedData: many ? added : added[0], type: level } });
      return;
    }
    const emit = () => this.outputSignal.emit({ action: many ? `DeselectingAll${LEVEL_PLURAL[level]}` : `DeselectingSingle${level}`, data: { checkedData: many ? removed : removed[0], type: level } });
    if (level === 'Territory') {
      emit();
      return;
    }
    const what = level === 'Region' ? (many ? 'all the regions' : 'this region') : (many ? 'all the districts' : 'this district');
    this.commonService.showConfirmation(`You are about to deselect ${what}, you want to continue this action?`)
      .then((res: any) => (res?.isConfirmed ? emit() : revert()));
  }
}
